import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, Gift, Copy, Users, Wallet } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

const STATUS_COLORS = {
  paid: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  processing: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  shipped: "text-sky-400 border-sky-500/30 bg-sky-500/10",
  delivered: "text-slate-300 border-white/20 bg-white/5",
  cancelled: "text-red-400 border-red-500/30 bg-red-500/10",
  refunded: "text-red-400 border-red-500/30 bg-red-500/10",
};

const TXN_LABELS = {
  purchase_earn: "Order reward",
  referral_earn: "Referral reward",
  welcome: "Welcome credit",
  redemption: "Applied to order",
  manual_adjust: "Account adjustment",
  reversal: "Reversal",
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState(null);

  useEffect(() => {
    api.get("/orders").then(({ data }) => {
      setOrders(data);
      setLoading(false);
    });
    api.get("/rewards/me").then(({ data }) => setRewards(data)).catch(() => {});
  }, []);

  const referralLink = rewards ? `${window.location.origin}/?ref=${rewards.referral_code}` : "";
  const copyReferral = () => {
    navigator.clipboard?.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
      <Seo title="My Account | Dark Growers Genetics" description="View your Dark Growers Genetics order history and account details." noindex />
      <p className="label-caps text-emerald-500 mb-3">My Account</p>
      <h1 className="font-display text-5xl text-slate-100">Hello, {user?.name.split(" ")[0]}</h1>
      <p className="text-slate-500 text-sm mt-2">{user?.email}</p>

      {/* Rewards */}
      <h2 className="font-display text-3xl text-slate-100 mt-14 mb-6 flex items-center gap-2"><Gift size={24} className="text-emerald-500" /> Rewards</h2>
      <div className="grid sm:grid-cols-3 gap-4" data-testid="rewards-summary">
        <div className="border border-emerald-500/30 bg-emerald-500/[0.06] p-6">
          <p className="label-caps text-slate-400 text-[0.6rem] flex items-center gap-1.5"><Wallet size={13} /> Credit Balance</p>
          <p className="font-display text-4xl text-emerald-400 mt-2" data-testid="rewards-balance">${(rewards?.balance ?? 0).toFixed(2)}</p>
        </div>
        <div className="border border-white/10 bg-[#0F0F0F] p-6">
          <p className="label-caps text-slate-400 text-[0.6rem] flex items-center gap-1.5"><Users size={13} /> Successful Referrals</p>
          <p className="font-display text-4xl text-slate-100 mt-2" data-testid="rewards-referral-count">{rewards?.referral_count ?? 0}</p>
        </div>
        <div className="border border-white/10 bg-[#0F0F0F] p-6 sm:col-span-1 flex flex-col justify-center">
          <p className="text-xs text-slate-400 leading-relaxed">
            Earn <b className="text-emerald-400">store credit</b> on every shipped order, plus <b className="text-emerald-400">${(rewards?.referral_reward ?? 25).toFixed(0)} in store credit</b> when a friend you refer makes their first purchase.
          </p>
        </div>
      </div>

      <div className="border border-white/10 bg-[#0F0F0F] p-6 mt-4">
        <p className="label-caps text-slate-400 text-xs">Your Referral Link</p>
        <div className="flex gap-2 mt-2">
          <input readOnly value={referralLink} data-testid="referral-link" className="flex-1 bg-[#050505] border border-white/10 text-sm text-slate-300 px-3 py-2.5 outline-none" />
          <button onClick={copyReferral} data-testid="copy-referral" className="inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors px-4 py-2.5 text-sm">
            <Copy size={14} /> Copy
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Share this link — your friend gets ${(rewards?.welcome_credit ?? 10).toFixed(0)} in store credit toward their first order, and you earn ${(rewards?.referral_reward ?? 25).toFixed(0)} in store credit when they complete it. Store credit only — no cash value.</p>
      </div>

      {rewards?.transactions?.length > 0 && (
        <div className="border border-white/10 bg-[#0F0F0F] mt-4 overflow-x-auto" data-testid="rewards-history">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0F0F0F] text-left label-caps text-slate-500 text-[0.6rem] border-b border-white/10">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Activity</th>
                <th className="px-4 py-3">Note</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rewards.transactions.map((t, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-slate-200">{TXN_LABELS[t.type] || t.type}</td>
                  <td className="px-4 py-3 text-slate-500 max-w-xs truncate">{t.note}</td>
                  <td className={`px-4 py-3 text-right font-medium ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {t.amount >= 0 ? "+" : "-"}${Math.abs(t.amount).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="font-display text-3xl text-slate-100 mt-14 mb-6">Order History</h2>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 bg-[#0F0F0F] animate-pulse" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="border border-white/10 bg-[#0F0F0F] p-12 text-center">
          <Package size={36} strokeWidth={1} className="text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500 text-sm">You haven't placed any orders yet.</p>
          <Link to="/shop" className="text-emerald-400 text-sm mt-3 inline-block">Start shopping</Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border border-white/10 bg-[#0F0F0F] p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
                <div>
                  <p className="text-slate-200 text-sm">#{o.payment?.invoice || o.id.slice(-6)}</p>
                  <p className="text-slate-500 text-xs mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`label-caps px-3 py-1 border text-[0.6rem] ${STATUS_COLORS[o.status] || ""}`}>
                  {o.status}
                </span>
              </div>
              <div className="mt-4 space-y-2">
                {o.items.map((it, idx) => (
                  <div key={idx} className="flex justify-between text-sm text-slate-400">
                    <span>{it.name} × {it.quantity}</span>
                    <span>${(it.price * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-4 pt-3 border-t border-white/10">
                <span className="text-slate-500 text-sm">Total</span>
                <span className="text-emerald-400">${o.total.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
